import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bus, LogOut, Megaphone, MapPin, Loader2, CheckCircle2, Users, QrCode, X, CalendarDays } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QrScanner } from "@/components/QrScanner";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DistrictNoticeBanner } from "@/components/DistrictNoticeBanner";


export default function DriverPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [announcing, setAnnouncing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const watchRef = useRef(null);
  const lastSentRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/driver/bus");
      setData(d);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "driver") { nav("/login"); return; }
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [user, load, nav]);

  useEffect(() => () => { if (watchRef.current !== null) navigator.geolocation?.clearWatch(watchRef.current); }, []);

  const startShare = () => {
    if (sharing) return true;
    if (!navigator.geolocation) { toast.error("This device has no GPS support."); return false; }
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentRef.current < 4000) return;
        lastSentRef.current = now;
        api.post("/driver/bus/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
      },
      () => toast.error("Could not read GPS — check location permissions."),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    setSharing(true);
    return true;
  };

  const announce = async (school) => {
    setAnnouncing(true);
    try {
      await api.post("/driver/bus/announce", { company_id: school?.id || "" });
      if (school) setActiveSchool(school);
      toast.success(`Bus ${data?.bus?.number} is BOARDING NOW on ${school ? `${school.name}'s` : "the"} pickup board`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setAnnouncing(false);
      setPickOpen(false);
    }
  };

  const boardingNow = () => {
    if ((data?.schools?.length || 0) > 1) { setPickOpen(true); return; }
    announce(null);
  };

  const mark = async (s, action) => {
    setBusy(s.id);
    try {
      await api.post(`/driver/bus/board/${s.id}`, { action });
      setData((d) => ({
        ...d,
        students: d.students.map((x) => (x.id === s.id ? { ...x, onboard: action === "board" } : x)),
        onboard_count: d.onboard_count + (action === "board" ? 1 : -1),
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const [scanOpen, setScanOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  const [activeSchool, setActiveSchool] = useState(null);

  useEffect(() => {
    if (data?.bus && !sharing) startShare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.bus?.id]);

  useEffect(() => {
    api.get("/branding").then(({ data }) => setBranding(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const schools = data?.schools || [];
    if (schools.length > 1) {
      setActiveSchool((cur) => (cur && schools.some((s) => s.id === cur.id) ? cur : schools[0]));
    }
  }, [data]);

  const visibleStudents = (data?.students || []).filter(
    (s) => (data?.schools?.length || 0) <= 1 || !activeSchool || s.school_name === activeSchool.name
  );

  const handleScan = useCallback(async (code) => {
    try {
      const { data: r } = await api.post("/driver/bus/scan", { code });
      setData((d) => {
        if (!d) return d;
        const students = d.students.map((x) => (x.id === r.student_id ? { ...x, onboard: r.action === "board" } : x));
        return { ...d, students, onboard_count: students.filter((x) => x.onboard).length };
      });
      return { label: r.student, sub: r.action === "board" ? "ON the bus" : "OFF the bus", tone: r.action === "board" ? "green" : "amber" };
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
      return null;
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-page">
      <DistrictNoticeBanner />
      <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {branding?.logo_data_url ? (
            <img src={branding.logo_data_url} alt="" data-testid="driver-school-logo"
                 className="h-10 w-10 rounded-xl object-contain border border-slate-100 bg-white p-0.5" />
          ) : (
            <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Bus className="h-5 w-5 text-amber-700" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {branding?.school_name || "Bus Driver"}
            </div>
            <div className="font-display font-semibold leading-tight text-slate-900" data-testid="driver-bus-number">
              {data?.bus ? `Bus ${data.bus.display_number || data.bus.number}` : "Driver console"}
              {data?.bus?.sub_number && data.bus.sub_number !== data.bus.number && <span className="ml-1 text-xs font-normal text-violet-700">(route {data.bus.number})</span>}
              <span className="ml-2 text-xs font-normal text-slate-400">{user?.name}</span>
            </div>
            {data?.bus?.status === "delayed" && (
              <span className="mt-0.5 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid="driver-bus-delayed">
                DELAYED {data.bus.delay_minutes ? `${data.bus.delay_minutes} MIN` : ""}{data.bus.status_note ? ` · ${data.bus.status_note}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/calendar")} data-testid="driver-calendar-btn" className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 inline-flex items-center justify-center hover:bg-slate-100 transition">
            <CalendarDays className="h-4 w-4" />
          </button>
          <button onClick={doLogout} data-testid="driver-logout" className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 inline-flex items-center justify-center hover:bg-slate-100 transition">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4">
        {error && <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-500 shadow-sm" data-testid="driver-error">{error}</div>}
        {data && (
          <>
            <button
              onClick={boardingNow}
              disabled={announcing}
              data-testid="driver-announce-btn"
              className="w-full rounded-xl bg-amber-500 text-slate-900 px-6 py-6 font-display font-extrabold text-2xl tracking-tight shadow-md shadow-amber-500/25 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60 transition flex items-center justify-center gap-3"
            >
              {announcing ? <Loader2 className="h-7 w-7 animate-spin" /> : <Megaphone className="h-7 w-7" />}
              BUS BOARDING NOW
            </button>
            <p className="text-center text-[11px] text-slate-500 -mt-2" data-testid="boarding-hint">
              {(data.schools?.length || 0) > 1
                ? `One tap: pick the school, and "Bus ${data.bus?.display_number || data.bus?.number} · now boarding" shows on that school's TV board.`
                : `One tap: shows "Bus ${data.bus?.display_number || data.bus?.number} · now boarding" on the TV board.`}
            </p>
            <button
              onClick={() => setScanOpen(true)}
              data-testid="driver-scan-btn"
              className="w-full rounded-xl border border-slate-200 bg-white p-4 font-semibold text-slate-900 transition hover:bg-slate-50 hover:shadow-sm active:scale-[0.99] flex items-center gap-3"
            >
              <div className="h-9 w-9 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                <QrCode className="h-4.5 w-4.5 text-primary" />
              </div>
              <span className="flex-1 text-left text-sm">
                Scan badges
                <span className="block text-[11px] font-normal text-slate-500">
                  Students scan their QR badge to hop on or off
                </span>
              </span>
            </button>
            <div
              data-testid="driver-gps-status"
              className={`w-full rounded-xl p-3 flex items-center gap-3 text-sm ${
                sharing ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-white border border-slate-200 text-slate-500"
              }`}
            >
              <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${sharing ? "bg-emerald-600/10" : "bg-slate-100"}`}>
                <MapPin className={`h-4 w-4 ${sharing ? "text-emerald-600" : "text-slate-400"}`} />
              </div>
              <span className="flex-1 font-medium">
                {sharing ? "Live GPS on — school sees the bus in real time" : "Turning on live GPS…"}
              </span>
              {sharing && <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />}
            </div>

            <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  <Users className="h-3.5 w-3.5 text-slate-400" /> My riders
                </div>
                <span className="text-xs text-slate-500" data-testid="driver-onboard-count">
                  {visibleStudents.filter((s) => s.onboard).length} / {visibleStudents.length} on board
                </span>
              </div>
              {(data.schools?.length > 1) && (
                <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60" data-testid="driver-school-switch">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">Now serving</div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.schools.map((sch) => {
                      const kids = data.students.filter((s) => s.school_name === sch.name);
                      const on = kids.filter((s) => s.onboard).length;
                      const active = activeSchool?.id === sch.id;
                      return (
                        <button
                          key={sch.id}
                          onClick={() => setActiveSchool(sch)}
                          data-testid={`driver-school-pill-${sch.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                            active
                              ? "bg-amber-500 text-slate-900 shadow-sm"
                              : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                          }`}
                        >
                          {sch.name}
                          <span className={`ml-1.5 text-[10px] ${active ? "text-slate-800/70" : "text-slate-400"}`}>
                            {on}/{kids.length}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {visibleStudents.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-400">
                  {data.students.length === 0 ? "No students assigned to this bus yet." : "No riders at this school yet."}
                </div>
              )}
              {visibleStudents.map((s) => (
                <div key={s.id} className="px-4 py-3 border-b border-slate-100 flex items-center gap-3" data-testid={`driver-student-${s.id}`}>
                  <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${s.onboard ? "bg-emerald-500" : "bg-slate-300"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 text-sm truncate">{s.full_name}</div>
                    <div className="text-xs text-slate-400">{s.grade || ""}</div>
                  </div>
                  {s.onboard ? (
                    <button
                      onClick={() => mark(s, "exit")}
                      disabled={busy === s.id}
                      data-testid={`driver-exit-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 transition"
                    >
                      {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />} Off bus
                    </button>
                  ) : (
                    <button
                      onClick={() => mark(s, "board")}
                      disabled={busy === s.id}
                      data-testid={`driver-board-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 transition"
                    >
                      {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} On bus
                    </button>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
      </main>
      {scanOpen && (
        <QrScanner
          title="Scan student badge"
          hint="Point the camera at the QR code on the student's badge. Each scan toggles them on / off the bus."
          onScan={handleScan}
          onClose={() => setScanOpen(false)}
        />
      )}
      {pickOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="board-picker" onClick={() => setPickOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display font-bold text-lg text-slate-900">Which school's board?</h2>
              <button onClick={() => setPickOpen(false)} data-testid="board-picker-close" className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-600 text-white shadow-md shadow-red-600/30 hover:bg-red-700 transition-colors ml-4">
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">This bus serves {data?.schools?.length} schools. Choose where you're boarding now.</p>
            <div className="space-y-2">
              {(data?.schools || []).map((sch) => (
                <button
                  key={sch.id}
                  onClick={() => announce(sch)}
                  disabled={announcing}
                  data-testid={`board-picker-school-${sch.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-4 text-left font-semibold text-slate-900 hover:border-amber-400 hover:bg-amber-50 active:scale-[0.99] disabled:opacity-60 transition flex items-center gap-3"
                >
                  <Megaphone className="h-5 w-5 text-amber-500 shrink-0" />
                  <span className="flex-1">
                    {sch.name}
                    <span className="block text-[11px] font-normal text-slate-500">
                      Call boarding on {sch.name}'s TV board
                    </span>
                  </span>
                  {announcing && activeSchool?.id === sch.id && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
